import { z } from 'zod';
import { LedgerEntrySetSchema, LedgerKindSchema } from '../ledger.js';
import { VatTreatmentSchema } from '../vat.js';
import { CalendarDateOnlySchema, CalendarDateSchema, MAX_MINOR_UNITS, WarningDetailSchema, createPaginatedResponseSchema, writeWarningsFields } from './common.js';
import { LineTransferSchema } from './bank-transfer.js';
import { createAggregateQuerySchema, createAggregateResponseSchema } from './aggregate.js';
// =============================================================================
// Bank Statement Line
// =============================================================================
export const BankStatementLineSchema = z.object({
    id: z.string(),
    bankAccountId: z.string(),
    /**
     * Statement that imported this line. Null for legacy rows imported before the
     * bank-statement domain existed, and for lines added without a statement.
     */
    bankStatementId: z.string().nullable(),
    date: z.string(),
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    description: z.string(),
    /**
     * Zero-based source row index from the transformed CSV, scoped per statement.
     * Null for legacy rows imported before the bank-statement domain was added,
     * and for lines created manually (without a statement). Used as a
     * collision-proof join key when mapping allocation intent to imported lines.
     */
    rowIndex: z.number().int().nullable(),
    /** Non-null once the line has been allocated to a ledger entry set. */
    ledgerEntrySetId: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * Response for the hard-delete of an unallocated bank statement line
 * (`DELETE /accounts/:accountId/bank-statement-lines/:lineId`). Echoes the id of
 * the removed line so a caller (e.g. the CLI) can confirm which row went. Bare
 * semantic-id shape (no boolean flag), mirroring `DeleteEmptyLedgerResponseSchema`
 * - a 200 already signals success, and `z.literal(true)` is rejected by the
 * zod-compat lint rule (boolean enums break Gemini's Vertex AI function schema).
 */
export const DeleteBankStatementLineResponseSchema = z.object({
    deletedLineId: z.string().uuid(),
});
export const StatementAllocationSummarySchema = z.object({
    totalLines: z.number().int().min(0),
    allocatedLines: z.number().int().min(0),
    /** Absolute (unsigned) sum of all line amounts on the statement. */
    totalAbsAmountMinorUnits: z.number().int().min(0),
    /** Absolute (unsigned) sum of allocated line amounts. */
    allocatedAbsAmountMinorUnits: z.number().int().min(0),
    currency: z.string().length(3),
});
/**
 * Slim response for the bank-account-level allocation surface: total
 * unallocated lines and the live-proposal subset. Both are computed through
 * the proposal-aware list predicate; no rows or proposal-id sets are loaded.
 */
export const BankAccountAllocationCountSchema = z.object({
    unallocatedLines: z.number().int().min(0),
    proposedLines: z.number().int().min(0),
});
/**
 * Response for GET /accounts/:accountId/bank-accounts/:bankAccountId/imported-line-coverage.
 *
 * How much bank history the account actually holds: the transaction-date span
 * of every imported statement line and how many there are, across every
 * statement ever imported (and legacy lines with no statement). This is
 * COVERAGE, not pipeline status - it answers "what period does the confirmed
 * balance run to", which is what the statement-count line it replaces was only
 * ever indirect evidence for.
 *
 * Computed as a single aggregate (COUNT + MIN + MAX over the indexed
 * `bank_account_id`): one round trip and an index-only scan, but linear in
 * the account's line count rather than constant-time - the COUNT walks every
 * matching index entry.
 *
 * `firstTransactionDate` / `lastTransactionDate` are ISO 8601 timestamps at
 * UTC midnight, matching how every sibling route in this domain serialises a
 * line's `date` (a `@db.Date` column read back through `toISOString()`);
 * consumers take the `YYYY-MM-DD` head before formatting. Both are null
 * exactly when `importedLineCount` is 0 - a bank account with no imported
 * lines yet.
 */
export const BankAccountImportedLineCoverageSchema = z.object({
    importedLineCount: z.number().int().min(0),
    firstTransactionDate: z.string().nullable(),
    lastTransactionDate: z.string().nullable(),
});
export const AllocateBankStatementLineSplitSchema = z.object({
    ledgerAccountId: z.string().uuid(),
    /**
     * Signed minor-unit amount in ledger-entry sign convention (+ = debit, - = credit).
     * The full set of splits, combined with the bank leg, must sum to zero.
     *
     * Bounded to the PostgreSQL int4 range (`MAX_MINOR_UNITS`, ±R21,474,836.47):
     * each leg persists to `LedgerEntry.amountMinorUnits`, an int4 column. The
     * zero-sum check only validates the aggregate, so without a per-leg bound a
     * balanced pair of individually oversized offsetting legs would pass the wire
     * schema and then overflow int4 during Prisma persistence - a non-AppError
     * 500, with earlier lines in a batch already committed. The bound rejects
     * those at the HTTP boundary instead. Raising it needs the column migrated to
     * BigInt first.
     */
    amountMinorUnits: z
        .number()
        .int()
        .min(-MAX_MINOR_UNITS)
        .max(MAX_MINOR_UNITS),
    /**
     * Optional per-split VAT treatment chosen by the allocation caller. The UI
     * derives concrete net/VAT legs before posting, but the wire contract still
     * carries the user's treatment choice so every caller shares the same split
     * shape and idempotency payload.
     */
    vatTreatment: VatTreatmentSchema.optional(),
});
/**
 * One invoice that a bank statement line was matched to (post-allocation).
 * Used by the row-inline "Matched to INV-XXXX" subtitle on allocated lines.
 */
export const MatchedInvoiceSchema = z.object({
    invoiceId: z.string().uuid(),
    invoiceNumber: z.string().nullable(),
    amountMinorUnits: z.number().int(),
});
/**
 * One expense (supplier invoice) that a money-out bank statement line was
 * settled against (post-allocation). Used by the row-inline "Matched to
 * <supplier>" deep link on settled money-out lines. Expenses have no
 * human-facing number, so the supplier name + reference identify the row.
 */
export const MatchedExpenseSchema = z.object({
    supplierInvoiceId: z.string().uuid(),
    supplierName: z.string(),
    supplierInvoiceNumber: z.string().nullable(),
    /** Remaining references, so the deep link names the expense by whatever the document actually printed. */
    supplierOwnReference: z.string().nullable(),
    customerReference: z.string().nullable(),
    customerAccountNumber: z.string().nullable(),
    amountMinorUnits: z.number().int(),
});
export const MatchableInvoiceSchema = z.object({
    invoiceId: z.string().uuid(),
    invoiceNumber: z.string().nullable(),
    /** EFT reference the client was told to put on the deposit. Often the strongest signal in bank narration. */
    paymentReference: z.string().nullable(),
    /** Snapshotted project reference (matter code) at invoice issue time. */
    reference: z.string().nullable(),
    clientId: z.string().uuid(),
    clientName: z.string(),
    totalMinorUnits: z.number().int(),
    /**
     * What is still owed on the invoice. The live scorer returns rows with a
     * positive figure; proposal re-hydration reads the ledger's tagged Accounts
     * Receivable net for a PAYMENT leg, which is 0 once every settlement against
     * the invoice, reconciled to a bank line or not, covers the total.
     */
    outstandingMinorUnits: z.number().int(),
    currency: z.string(),
    date: z.string(),
    status: z.enum(['APPROVED', 'SENT', 'PAID']),
    /**
     * Settlement path for this match. Omitted by the live scorer (always an
     * unpaid mint); set to `'paid'` by proposal re-hydration of a PAYMENT leg,
     * where an existing recorded payment already covers the invoice and the
     * bank line links it (drains Payments Clearing) instead of minting a new one.
     * Consumers default a missing value to `'unpaid'`.
     */
    classification: z.enum(['unpaid', 'paid']).optional(),
    /**
     * Present only when `classification === 'paid'`: the recorded payment the
     * match links. Mirrors `ReconcilableInvoice.linkedPayment` so the inline cell
     * can stage a paid match directly from a proposal suggestion.
     */
    linkedPayment: z
        .object({
        paymentId: z.string().uuid(),
        amountAllocated: z.number().int(),
        paymentTotalMinorUnits: z.number().int(),
    })
        .optional(),
});
/**
 * One supplier invoice (expense) that a money-out bank statement line can be
 * allocated to. Mirrors MatchableInvoice for the outgoing-payment picker.
 *
 * `outstandingMinorUnits` is the amount still unpaid (total minus prior bank-line
 * settlement). It is present on auto-suggestion matches - where it is the amount
 * the suggestion staged and must be validated against - and omitted by the manual
 * picker, which lists DRAFT expenses with no posted journal (so no well-defined
 * outstanding) and shows the total. Consumers fall back to `totalMinorUnits` when
 * it is absent.
 *
 * Identified by supplier name (expenses have no human-facing number today), with
 * the supplier's own reference as a secondary signal.
 */
export const MatchableExpenseSchema = z.object({
    supplierInvoiceId: z.string().uuid(),
    supplierId: z.string().uuid(),
    supplierName: z.string(),
    /** The supplier's own invoice number, when captured. */
    supplierInvoiceNumber: z.string().nullable(),
    /**
     * The other references the document and the supplier relationship carry. A
     * bank narration frequently quotes one of these rather than the document
     * number - the account code especially, since suppliers commonly use it as
     * the payment reference - so the picker must be able to show and search them.
     */
    supplierOwnReference: z.string().nullable(),
    customerReference: z.string().nullable(),
    customerAccountNumber: z.string().nullable(),
    totalMinorUnits: z.number().int(),
    /** Unpaid balance; present on auto-suggestion matches, omitted by the manual picker. */
    outstandingMinorUnits: z.number().int().optional(),
    currency: z.string(),
    date: z.string(),
    /** DRAFT/APPROVED for unpaid matches; PAID when a PAYMENT leg re-hydrates a settled expense. */
    status: z.enum(['DRAFT', 'APPROVED', 'PAID']),
    /**
     * Settlement path. Omitted by the (retired) live scorer; set to `'paid'` by
     * proposal re-hydration of a PAYMENT leg, where an existing recorded OUT
     * payment already covers the expense and the bank line links it. Consumers
     * default a missing value to `'unpaid'`.
     */
    classification: z.enum(['unpaid', 'paid']).optional(),
    /**
     * Present only when `classification === 'paid'`: the recorded OUT payment the
     * match links. Mirrors `ReconcilableSupplierInvoice.linkedPayment`.
     */
    linkedPayment: z
        .object({
        paymentId: z.string().uuid(),
        amountAllocated: z.number().int(),
        paymentTotalMinorUnits: z.number().int(),
    })
        .optional(),
});
/**
 * One credit note that a money-out bank statement line can be settled against
 * (the refund picker). Only issued credit notes on the same ledger with
 * outstanding credit balance are returned.
 *
 * `outstandingMinorUnits` = `totalMinorUnits` minus the sum of existing credit
 * note match settlements against this credit note's entry set. Always > 0 for
 * rows returned by the picker.
 *
 * `clientCreditMinorUnits` = the client's live net AR position (positive = in
 * credit, zero or negative = owes the practice on other invoices).
 * Informational only, never a refund limit - a credit note is refundable up to
 * its own `outstandingMinorUnits` regardless of this figure
 * (`.claude/rules/record-what-happened.md`).
 */
export const MatchableCreditNoteSchema = z.object({
    creditNoteId: z.string().uuid(),
    creditNoteNumber: z.string().nullable(),
    clientId: z.string().uuid(),
    clientName: z.string(),
    invoiceId: z.string().uuid(),
    invoiceNumber: z.string().nullable(),
    totalMinorUnits: z.number().int(),
    outstandingMinorUnits: z.number().int(),
    clientCreditMinorUnits: z.number().int(),
    currency: z.string(),
    /** ISO 8601 calendar date (YYYY-MM-DD) of the credit note. */
    date: z.string(),
});
export const ListMatchableCreditNotesQuerySchema = z.object({
    /** Free-text substring across credit note number and client name. Empty/omitted returns all candidates. */
    search: z.string().trim().max(200).optional().default(''),
    limit: z.coerce.number().int().min(1).max(100).optional().default(25),
});
export const ListMatchableCreditNotesResponseSchema = z.object({
    data: z.array(MatchableCreditNoteSchema),
});
/**
 * One credit note that a money-out bank statement line was settled against
 * (post-allocation). Used by the row-inline "Matched to CN-XXXX" subtitle on
 * allocated money-out lines.
 */
export const MatchedCreditNoteForLineSchema = z.object({
    creditNoteId: z.string().uuid(),
    creditNoteNumber: z.string().nullable(),
    invoiceId: z.string().uuid(),
    amountMinorUnits: z.number().int(),
});
export const CreateBankStatementLineInputSchema = z.object({
    date: CalendarDateSchema,
    amountMinorUnits: z.number().int().refine((value) => value !== 0, 'Amount cannot be zero.'),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO 4217 code (e.g. ZAR)').optional().default('ZAR'),
    description: z.string().min(1, 'Description is required').max(500, 'Description must be 500 characters or less'),
    idempotencyKey: z.string().min(1),
});
// PATCH /accounts/:accountId/bank-statement-lines/:lineId
// Edit a bank statement line's description (the only editable field). Same
// bounds as the create-time description so an edit can never store a value a
// create would reject (blank, or over 500 chars).
export const UpdateBankStatementLineDescriptionInputSchema = z.object({
    description: z.string().min(1, 'Description is required').max(500, 'Description must be 500 characters or less'),
});
export const BankStatementLineListResponseSchema = createPaginatedResponseSchema(BankStatementLineSchema);
/**
 * Every FILTER field shared by the bank-transaction list and its aggregate,
 * independent of pagination, sort, or (for the aggregate) grouping. Both query
 * schemas extend this one object, so a total and a listing cover the same
 * population by construction.
 */
export const BankStatementLineFiltersQuerySchema = z.object({
    /** Inclusive bounds on the transaction date (the day the bank booked it). */
    from: CalendarDateSchema.optional(),
    to: CalendarDateSchema.optional(),
    /** Restrict to rows imported by this bank statement. */
    bankStatementId: z.string().uuid().optional(),
    /** Filter by allocation status: 'true' = allocated only, 'false' = unallocated only; omit for all lines. */
    allocated: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    /**
     * Filter the unallocated backlog by live proposal presence. `true` means
     * an undismissed proposal exists; `false` means dismissed or absent.
     */
    hasProposal: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    /**
     * Free-text search. Matches the line's description (case-insensitive substring) OR, when
     * the text is amount-shaped, the line's amount. The amount band spans whatever precision
     * is left untyped: "1750" matches R1,750.00-R1,750.99, "1750.8" matches R1,750.80-R1,750.89,
     * "1750.80" matches exactly. The sign is ignored, so money-in and money-out of that amount
     * both match. A term that is exactly a UUID additionally matches the line's own id, which
     * is how a caller addresses one specific line by URL (the journals UI links a bank-line
     * journal to `?search=<lineId>`). Whitespace is trimmed; empty strings are rejected so
     * callers cannot pass `?search=` and accidentally narrow nothing.
     */
    search: z.string().trim().min(1).max(200).optional(),
    /**
     * Arrival window over `createdAt`: when the line reached Bill (feed POST or
     * statement import), not when the bank booked it. `createdFrom` is inclusive,
     * `createdBefore` exclusive, so consecutive windows share no instant. A
     * back-dated feed line or a late statement import falls in the window it
     * arrived in, whatever its transaction date. Instants, not calendar days -
     * where a day starts depends on the caller's timezone - and a single
     * datetime shape rather than a date-or-datetime union, which reaches the chat
     * model as an `anyOf`.
     */
    createdFrom: z.iso.datetime({ offset: true }).optional(),
    createdBefore: z.iso.datetime({ offset: true }).optional(),
});
// Applied to each composed query rather than to the filters object, because a
// refined object cannot be extended.
function refineAllocationFilters(value, ctx) {
    if (value.allocated === true && value.hasProposal !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['hasProposal'],
            message: 'hasProposal cannot be combined with allocated=true',
        });
    }
}
export const ListBankStatementLinesQuerySchema = z
    .object({
    page: z.coerce.number().int().min(1).prefault(1),
    limit: z.coerce.number().int().min(1).max(100).prefault(20),
    ...BankStatementLineFiltersQuerySchema.shape,
    /**
     * Sort order by date (and createdAt as tiebreaker). Defaults to `desc` (newest first).
     * Use `asc` for the "To allocate" section so the user works oldest-first.
     */
    order: z.enum(['asc', 'desc']).optional(),
})
    .superRefine(refineAllocationFilters);
/**
 * Where a line stands in the allocation workflow - the three sections of the
 * bank-account screen. `awaitingApproval` is unallocated with a live
 * (undismissed) proposal, `toAllocate` is unallocated with none. Exactly the
 * populations of `allocated=true`, `hasProposal=true` and `hasProposal=false`.
 */
export const BankStatementLineAllocationStateSchema = z.enum(['allocated', 'awaitingApproval', 'toAllocate']);
/** `in` is a positive amount (money received), `out` a negative one (money paid). */
export const BankStatementLineDirectionSchema = z.enum(['in', 'out']);
/**
 * Dimensions `aggregateBankAccountTransactions` can group by. Neither is a
 * column: allocation state is a relation predicate and direction is the sign of
 * the amount, so each group is a filtered read over the list's own where-clause.
 */
export const AggregateBankAccountTransactionsDimensionSchema = z.enum(['allocation', 'direction']);
// Strict: an argument the route does not have is a 400 naming it, not a total
// over an unfiltered population presented as filtered.
export const AggregateBankAccountTransactionsQuerySchema = createAggregateQuerySchema(BankStatementLineFiltersQuerySchema, AggregateBankAccountTransactionsDimensionSchema)
    .strict()
    .superRefine(refineAllocationFilters);
export const AggregateBankAccountTransactionsResponseSchema = createAggregateResponseSchema({
    // `count` is the number of bank transactions (statement lines). Amounts carry
    // one entry per currency because figures in different currencies must never
    // be added together. Money in and money out are both positive magnitudes;
    // `netMinorUnits` is in less out, so it is negative when more left than arrived.
    measures: z.object({
        amounts: z.array(z.object({
            currency: z.string(),
            moneyInMinorUnits: z.int().min(0),
            moneyOutMinorUnits: z.int().min(0),
            netMinorUnits: z.int(),
        })),
    }),
    key: z.object({
        allocation: BankStatementLineAllocationStateSchema.optional(),
        direction: BankStatementLineDirectionSchema.optional(),
    }),
    filter: z.object({
        from: z.string().nullable(),
        to: z.string().nullable(),
        bankStatementId: z.string().nullable(),
        allocated: z.boolean().nullable(),
        hasProposal: z.boolean().nullable(),
        search: z.string().nullable(),
        createdFrom: z.string().nullable(),
        createdBefore: z.string().nullable(),
    }),
    dimension: AggregateBankAccountTransactionsDimensionSchema,
});
export const BankTransactionsExportQuerySchema = z.object({
    from: CalendarDateOnlySchema.optional(),
    to: CalendarDateOnlySchema.optional(),
    /** Restrict to rows imported by this bank statement. */
    bankStatementId: z.string().uuid().optional(),
    /** Filter by allocation status: 'true' = allocated only, 'false' = unallocated only; omit for all lines. */
    allocated: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
});
// =============================================================================
// Bank Statement Line Documents
// =============================================================================
export const BankStatementLineDocumentResponseSchema = z.object({
    id: z.string(),
    bankStatementLineId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    downloadUrl: z.string(),
});
export const BankStatementLineDocumentListResponseSchema = z.array(BankStatementLineDocumentResponseSchema);
// Sparse map keyed by bank statement line id - only lines with at least one
// document appear. Backs the attachment indicator on the transactions list.
export const BankStatementLineDocumentCountsResponseSchema = z.object({
    counts: z.object({}).catchall(z.number().int().nonnegative()),
});
// A document reachable from a statement import: its own CSV, or a file linked to
// it such as the bank's PDF statement. `isImportFile` marks the CSV the import
// was made from, which stays with the import and cannot be removed from it.
export const BankStatementDocumentResponseSchema = z.object({
    id: z.string(),
    statementId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    isImportFile: z.boolean(),
    downloadUrl: z.string(),
});
export const BankStatementDocumentListResponseSchema = z.array(BankStatementDocumentResponseSchema);
// =============================================================================
// Bank Statement
// =============================================================================
export const BankStatementStateSchema = z.enum([
    'UPLOADING', 'VALIDATING', 'IN_REVIEW', 'APPROVED', 'IMPORTED', 'FAILED',
]);
export const BankStatementSourceFormatSchema = z.enum(['CSV', 'PDF']);
/**
 * The `metadata` field of a PDF statement upload. Nothing is read from the file, so the user supplies the period
 * the statement covers; both dates are inclusive and the end may not fall before the start.
 */
export const UploadPdfBankStatementMetadataSchema = z.strictObject({
    idempotencyKey: z.string().min(1).max(255, 'Idempotency key must be 255 characters or less'),
    periodStart: CalendarDateOnlySchema,
    periodEnd: CalendarDateOnlySchema,
});
export const BankStatementReconciliationStatusSchema = z.enum(['RECONCILED', 'PARTIAL', 'NOT_APPLICABLE']);
export const FindingSchema = z.discriminatedUnion('kind', [
    z.object({ severity: z.literal('warn'), kind: z.literal('duplicate-within-csv'), rowIndex: z.number(), conflictsWithRowIndexes: z.array(z.number()), conflictsWithRowIndexesTotal: z.number().int().nonnegative().optional() }),
    z.object({ severity: z.literal('warn'), kind: z.literal('duplicate-in-db'), rowIndex: z.number(), matchedLineId: z.string() }),
    // overlapPeriodStart/overlapPeriodEnd are the overlapping statement's own
    // period, as ISO strings, so a finding can name which statement it
    // overlaps. Optional: MUST stay optional, since findings are written once
    // to Json at the IN_REVIEW transition and never rewritten - a stored
    // finding predating these fields must keep parsing.
    z.object({ severity: z.literal('warn'), kind: z.literal('period-overlap'), overlapsStatementId: z.string(), overlapDays: z.number(), overlapPeriodStart: z.string().optional(), overlapPeriodEnd: z.string().optional() }),
    z.object({ severity: z.literal('info'), kind: z.literal('account-number-mismatch'), fileAccountNumber: z.string() }),
    z.object({ severity: z.literal('warn'), kind: z.literal('balance-discontinuity'), previousStatementId: z.string(), gapMinorUnits: z.number() }),
    z.object({ severity: z.literal('warn'), kind: z.literal('bank-mismatch'), fileBank: z.string(), accountBank: z.string() }),
    z.object({ severity: z.literal('warn'), kind: z.literal('future-dated-transaction'), rowIndex: z.number() }),
    z.object({ severity: z.literal('warn'), kind: z.literal('before-ledger-opening'), rowIndex: z.number(), openingDate: z.string() }),
    z.object({ severity: z.literal('block'), kind: z.literal('currency-mismatch'), fileCurrency: z.string(), accountCurrency: z.string() }),
    // parse-error rows are blocking: financial records demand zero ambiguity.
    // If we couldn't extract a valid date or amount, the user must fix the
    // source file rather than silently dropping the row at import.
    z.object({ severity: z.literal('block'), kind: z.literal('parse-error'), rowIndex: z.number(), reason: z.string() }),
    // From a bank feed's start date the feed is the only writer of the bank
    // account's lines, so a statement row dated on or after it would duplicate
    // a feed line. feedStartDate is YYYY-MM-DD.
    z.object({ severity: z.literal('block'), kind: z.literal('bank-feed-owned'), rowIndex: z.number(), feedStartDate: z.string() }),
]);
/**
 * True for a block-severity finding that carries no `rowIndex` - a
 * statement-level defect (e.g. `currency-mismatch`) rather than a defect in
 * one row. This is the approval gate: a row-level block makes its own row
 * unimportable without making the statement unapprovable, so `approveStatement`
 * refuses only when this predicate matches at least one finding.
 */
export function isBlockingStatementFinding(finding) {
    return finding.severity === 'block' && !('rowIndex' in finding);
}
/**
 * True for any finding that carries a `rowIndex` - a defect in one specific
 * row, regardless of severity. Narrows to the row-level branch of the
 * `Finding` union so callers can safely read `.rowIndex`.
 */
export function isRowLevelFinding(finding) {
    return 'rowIndex' in finding;
}
/** Initial import selection excludes warning-bearing rows in finding order. */
export function defaultStatementExcludedRowIndexes(findings) {
    return [...new Set(findings.filter(isRowLevelFinding).filter((finding) => finding.severity === 'warn').map((finding) => finding.rowIndex))];
}
/**
 * True for a row-level finding whose severity is `block` - the set of rows a
 * reviewer's exclusion defaults to excluding (`filterRowsByExclusionSet`) and
 * that the CLI's approve-plan summary and preview list as unimportable.
 */
export function isRowLevelBlockFinding(finding) {
    return finding.severity === 'block' && isRowLevelFinding(finding);
}
export const BankStatementSchema = z.object({
    id: z.string().uuid(),
    bankAccountId: z.string().uuid(),
    state: BankStatementStateSchema,
    sourceFormat: BankStatementSourceFormatSchema,
    sourceBank: z.string().nullable(),
    originalFileName: z.string(),
    originalFileSha256: z.string(),
    originalFileBytes: z.number(),
    periodStart: z.string(),
    periodEnd: z.string(),
    openingBalanceMinorUnits: z.number().nullable(),
    closingBalanceMinorUnits: z.number().nullable(),
    currency: z.string(),
    parsedRowCount: z.number(),
    importedRowCount: z.number().nullable(),
    skippedZeroRowCount: z.number(),
    erroredRowCount: z.number(),
    duplicateRowCount: z.number(),
    reconciliationStatus: BankStatementReconciliationStatusSchema,
    findings: z.array(FindingSchema),
    /**
     * Human-readable reason the async import job failed, present only when
     * `state === 'FAILED'` AND the failure came from the import job (a string
     * value). Legacy upload/validation failures store an error OBJECT in the
     * same column and map to `null` here - the object shape was never meant
     * for display and predates this field. `null` for every other state.
     */
    failureReason: z.string().nullable(),
    createdAt: z.string(),
    importedAt: z.string().nullable(),
});
export const PreparedTransactionSchema = z.object({
    date: z.string(),
    amountMinorUnits: z.number(),
    currency: z.string(),
    description: z.string(),
    postingDate: z.string().nullable(),
    rowIndex: z.number(),
});
export const ReconciliationReportSchema = z.object({
    status: BankStatementReconciliationStatusSchema,
    openingBalanceMinorUnits: z.number().nullable(),
    closingBalanceMinorUnits: z.number().nullable(),
    totalCreditMinorUnits: z.number(),
    totalDebitMinorUnits: z.number(),
    mismatches: z.array(z.object({
        rowIndex: z.number(),
        expectedBalanceMinorUnits: z.number(),
        actualBalanceMinorUnits: z.number(),
        deltaMinorUnits: z.number(),
    })),
});
export const ValidationReportSchema = z.object({
    statement: BankStatementSchema,
    summary: z.object({
        totalRows: z.number(),
        cleanRows: z.number(),
        flaggedRows: z.number(),
        erroredRows: z.number(),
        skippedZeroRows: z.number(),
        duplicateInCsvCount: z.number(),
        duplicateInDbCount: z.number(),
    }),
    /** Any block-severity finding, row-level included. Does not by itself gate approval. */
    hasBlockingFinding: z.boolean(),
    /**
     * Block-severity findings carrying no `rowIndex` - a statement-level defect.
     * This is the approval gate: `approveStatement` refuses only when this is
     * true. See `isBlockingStatementFinding`.
     */
    hasBlockingStatementFinding: z.boolean(),
    findings: z.array(FindingSchema),
    flaggedRows: z.array(PreparedTransactionSchema),
    reconciliation: ReconciliationReportSchema.nullable(),
});
export const ApproveStatementInputSchema = z.strictObject({
    /**
     * Authoritative when present: every zero-based rowIndex the reviewer chose
     * not to import. Replaces the findings-minus-overrides derivation - the
     * import filter stops consulting `findings` altogether once this is set.
     * Absent or explicit `null` both mean "no exclusion decision" and fall
     * back to the legacy findings-minus-overrides path below.
     */
    excludedRowIndexes: z.array(z.number().int().nonnegative()).optional().nullable(),
    /**
     * Legacy. Read only when `excludedRowIndexes` is absent: the set of
     * warn-severity row findings the reviewer chose to import anyway (a delta
     * against the old default of "flagged rows are excluded unless overridden").
     *
     * Was required on `origin/main` before `excludedRowIndexes` existed; made
     * optional here so the frontend can send `excludedRowIndexes` alone. During
     * the deploy rollout window, a request from a freshly loaded new frontend
     * can transiently 400 against an old-revision backend pod that still
     * requires this field - accepted as a low-traffic, transient, user-retriable
     * cost per `.claude/rules/breaking-change-rollout.md`'s "when one PR is
     * fine" case, not a defect to route around (the frontend deliberately does
     * not send a placeholder `overriddenWarningRowIndexes: []` to paper over it,
     * since an old pod would apply that as "override nothing" and silently drop
     * every finding-bearing row).
     */
    overriddenWarningRowIndexes: z.array(z.number()).optional(),
});
export const BankStatementListResponseSchema = z.object({
    data: z.array(BankStatementSchema),
});
// Per-row status from the deterministic transform, used to drive the raw-CSV
// table's status icon. 'zero-value' and 'parse-error' rows are not imported.
export const RowLocalStatusSchema = z.enum(['ok', 'zero-value', 'parse-error']);
export const NormalizedCsvRowSchema = z.object({
    date: z.string(),
    description: z.string(),
    amountMinorUnits: z.number().int(),
    balanceMinorUnits: z.number().int().nullable(),
});
// Companions stay aligned to raw source rows, including null unreadable rows.
export const RawCsvSchema = z.object({
    fields: z.array(z.string()),
    rows: z.array(z.object({}).catchall(z.string())),
    rowStatuses: z.array(RowLocalStatusSchema),
    // Optional during rolling deployment: older backend pods omit this additive
    // display field and the frontend safely falls back to the raw source date.
    normalizedDates: z.array(z.string().nullable()).optional(),
    normalizedRows: z.array(NormalizedCsvRowSchema.nullable()).optional(),
});
// Review payload for a persisted statement: the validation report plus the raw
// CSV. Re-derived from the stored file on demand so the review screen can be
// reopened ("resume review") without the original file in memory.
export const BankStatementReviewQuerySchema = z.strictObject({
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    rowOffset: z.coerce.number().int().nonnegative().optional(),
    findingOffset: z.coerce.number().int().nonnegative().optional(),
    storedFindingOffset: z.coerce.number().int().nonnegative().optional(),
}).refine((query) => query.pageSize !== undefined || (query.rowOffset === undefined && query.findingOffset === undefined && query.storedFindingOffset === undefined), { message: 'pageSize is required when supplying review offsets' });
const ReviewPageSchema = z.object({
    offset: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    nextOffset: z.number().int().nonnegative().nullable(),
});
export const BankStatementReviewPaginationSchema = z.object({
    rows: ReviewPageSchema,
    findings: ReviewPageSchema,
    storedFindings: ReviewPageSchema,
});
export const BankStatementImportTotalsSchema = z.object({
    moneyInMinorUnits: z.number().int().nonnegative(),
    moneyOutMinorUnits: z.number().int().nonnegative(),
});
export const BankStatementReviewTotalsInputSchema = z.strictObject({
    excludedRowIndexes: z.array(z.number().int().nonnegative()).max(50000),
});
export const BankStatementReviewSchema = z.object({
    importTotals: BankStatementImportTotalsSchema.optional(),
    report: ValidationReportSchema,
    csv: RawCsvSchema,
    pagination: BankStatementReviewPaginationSchema.optional(),
});
// =============================================================================
// Bank Statement Line - Allocation
// =============================================================================
export const UnallocateBankStatementLineInputSchema = z.object({
    /**
     * Caller-supplied idempotency key. The service wraps the side-effecting
     * work of the unreconcile (ledger entry set reversal + atomic allocation
     * clear) in an `ensureIdempotent` envelope keyed on
     * `unreconcile-bank-line-{lineId}-{idempotencyKey}`. A retry with the same
     * key after a network failure returns the cached result without posting a
     * second reversal. Use a UUID generated once per unallocate intent and keep
     * it stable across retries.
     */
    idempotencyKey: z.string().min(1).max(255),
});
export const UnallocateBankStatementLineResponseSchema = z.object({
    /** The bank statement line after unallocation. ledgerEntrySetId will be null. */
    line: BankStatementLineSchema,
    ...writeWarningsFields,
});
// =============================================================================
// Reconcile Money-In Bank Line
// =============================================================================
/**
 * Invoice allocation leg for the reconcile endpoint: an invoice to settle and the
 * amount applied. The service mints one Payment per distinct client group via
 * `recordPaymentForInvoices`, posts the AR-clearing journal (Cr AR / Dr Payments
 * Clearing) for each, then drains Clearing with a bank-leg entry set.
 */
export const ReconcileMoneyInInvoiceSchema = z.object({
    invoiceId: z.string().uuid(),
    /**
     * Positive minor-unit amount applied to this invoice. Must not exceed the
     * invoice's current outstanding balance (total minus prior payments and
     * bank-line matches). Validated service-side.
     */
    amountMinorUnits: z.number().int().min(1),
});
/**
 * A document-free client allocation to mint on a reconcile: a business
 * credit/debit (business bank) or a trust receipt/payment (trust bank), with
 * no invoice/expense behind it.
 *
 * Resolved direction by endpoint and bank kind:
 *   - Money-in, business bank  -> a client credit (Cr Accounts Receivable).
 *   - Money-in, trust bank     -> a trust receipt (Cr Trust Creditors).
 *   - Money-out, trust bank    -> a trust payment (Dr Trust Creditors).
 *   - Money-out, business bank -> a client debit (Dr Accounts Receivable,
 *     clientId-tagged, document-untagged) - drains a document-free
 *     overpayment credit left by an earlier money-in client credit, with no
 *     credit note required. A document-backed refund against an existing
 *     credit note still goes through the credit-note refund match path
 *     instead.
 *
 * Use this when a bank line pays a client directly with no invoice/expense
 * behind it (e.g. a client deposit held on account, an overpayment refund, or
 * a trust disbursement). No duplicate `clientId` within one request (400);
 * each `clientId` must belong to the caller's account (404 otherwise,
 * verified at the route).
 */
export const ReconcileClientAllocationSchema = z.object({
    clientId: z.string().uuid(),
    /** Positive minor-unit amount minted as this client's leg. */
    amountMinorUnits: z.number().int().min(1),
});
/**
 * Request body for POST /accounts/:accountId/bank-statement-lines/:lineId/reconcile.
 *
 * Reconciles a money-in bank statement line with one or more already-recorded
 * Payments, and optionally mints new Payments from invoice allocations and/or
 * client allocations. Posts a single drain entry set: Dr Bank / Cr Payments
 * Clearing / splits.
 *
 * - `paymentIds`: existing RECORDED, unreconciled, ledgered Payments to link.
 * - `invoices`: invoices to settle; the service records one Payment per distinct
 *   client and links those payments automatically (callers never manage Payments).
 * - `clientAllocations`: document-free client mints - a business credit or a
 *   trust receipt, depending on the line's bank account kind. See
 *   `ReconcileClientAllocationSchema`.
 * - `splits`: free-form ledger account targets for any residual (e.g. a deposit
 *   that partially settled an invoice with the remainder landing in a suspense
 *   account). Signed minor-unit amounts, debit-positive.
 *
 * Balance equation: `line.amountMinorUnits + splitSum - clearingSum === 0`, where
 * `clearingSum` is the sum of all linked payment amounts (existing + minted
 * invoice + minted client allocations).
 */
export const ReconcileMoneyInInputSchema = z.object({
    /**
     * IDs of already-recorded Payments to link to this bank line. Each must be
     * RECORDED, unreconciled (bankStatementLineId is null), and own a settlement
     * journal (ledgerEntrySetId is non-null). Capped at 50.
     */
    paymentIds: z.array(z.string().uuid()).max(50).default([]),
    /**
     * Invoices to settle. No duplicate invoiceId; each amount must not exceed the
     * invoice's outstanding balance. Under the hood the service records one Payment
     * per distinct client (posting the AR-clearing journal) and links it to this
     * line - callers do not manage Payments directly. Capped at 50.
     */
    invoices: z.array(ReconcileMoneyInInvoiceSchema).max(50).default([]),
    /**
     * Client allocations to mint: a business credit (business bank) or a trust
     * receipt (trust bank). No duplicate clientId. Defaults to an empty array -
     * omit entirely when not allocating directly to a client. Capped at 50.
     */
    clientAllocations: z.array(ReconcileClientAllocationSchema).max(50).default([]),
    /**
     * Free-form ledger account splits for any residual amount. The clearing leg
     * covers all payment amounts; any gap between the bank line amount and the
     * clearing sum must be covered by splits. Capped at 50.
     */
    splits: z.array(AllocateBankStatementLineSplitSchema).max(50).default([]),
    /**
     * Caller-supplied idempotency key. A retry with the same key is a no-op.
     * Use a UUID generated once per reconcile intent and keep it stable across
     * retries.
     */
    idempotencyKey: z.string().min(1).max(255),
});
/**
 * `warnings` is `[]` when the reconcile raised nothing to flag, and carries one
 * plain-English entry per s 86(4) trust-investment conduct rule the posted
 * journal touched (money withdrawn from an investment account; a leg tagged with
 * a client other than the one the account is held for). They never block the
 * write - the backend records what happened - so they are a moment to steer the
 * operator, not an error. No PII: the text names rules, never a client.
 */
export const ReconcileMoneyInResponseSchema = z.object({
    line: BankStatementLineSchema,
    entrySet: LedgerEntrySetSchema,
    warnings: z.array(z.string()),
});
// =============================================================================
// Reconcile Money-Out Bank Line
// =============================================================================
/**
 * Supplier invoice settlement leg for the reconcile-money-out endpoint: a
 * supplier invoice to settle and the amount applied. The service mints one OUT
 * Payment per distinct supplier via `recordPaymentForSupplierInvoices`, posts
 * the AP-clearing journal (Dr AP / Cr Payments Clearing) for each, then drains
 * Clearing with a bank-leg entry set.
 */
export const ReconcileMoneyOutSupplierInvoiceSchema = z.object({
    supplierInvoiceId: z.string().uuid(),
    /**
     * Positive minor-unit amount applied to this supplier invoice. Must not exceed
     * the invoice's current outstanding balance. Validated service-side.
     */
    amountMinorUnits: z.number().int().min(1),
});
/**
 * One credit-note refund leg for the reconcile-money-out endpoint: a credit
 * note to refund and the amount applied. The service groups refunds by the
 * credit note's client and mints one OUT Payment per distinct client via
 * `creditNoteService.recordRefundForCreditNotes`, posting Dr Accounts
 * Receivable (creditNoteId-tagged) for each, then drains Payments Clearing
 * with the bank-leg entry set. Replaces the retired `creditNoteMatches` arm of
 * the now-fully-retired direct-allocation endpoint.
 */
export const ReconcileMoneyOutCreditNoteRefundSchema = z.object({
    creditNoteId: z.string().uuid(),
    /**
     * Positive minor-unit amount applied to this credit note. Must not exceed
     * the credit note's refundable amount (total minus everything already
     * consumed from it - a prior cash refund plus any issue-time application
     * to the origin invoice). Validated service-side.
     */
    amountMinorUnits: z.number().int().min(1),
});
/**
 * Document-free supplier allocation leg for the reconcile-money-out endpoint -
 * the supplier-party analogue of `ReconcileClientAllocationSchema`. Mints a
 * zero-allocation OUT `Payment` tagged with `supplierId` and no
 * supplier-invoice behind it: an on-account payment to a supplier with no
 * matching bill (an overpayment, a double payment, or a deposit paid ahead of
 * an invoice), posting Dr Accounts Payable (supplierId-tagged,
 * document-untagged) via the document-free settlement path.
 *
 * BUSINESS bank lines only - `recordPayment` throws on a TRUST payment
 * carrying a `supplierId` (a trust ledger has no creditors of its own), so a
 * non-empty array on a TRUST money-out line is rejected with 400 before any
 * mint. No duplicate `supplierId` within one request (400); each `supplierId`
 * must belong to the caller's account and be active (404/archived rejected,
 * verified at the route).
 */
export const ReconcileSupplierAllocationSchema = z.object({
    supplierId: z.string().uuid(),
    /** Positive minor-unit amount minted as this supplier's leg. */
    amountMinorUnits: z.number().int().min(1),
});
/**
 * Request body for POST /accounts/:accountId/bank-statement-lines/:lineId/reconcile-money-out.
 *
 * Reconciles a money-out bank statement line with one or more already-recorded
 * OUT Payments, and optionally mints new OUT Payments from supplier invoice
 * allocations, credit-note refunds, and/or supplier allocations. Posts a
 * single drain entry set: Cr Bank / Dr Payments Clearing / splits.
 *
 * - `paymentIds`: existing RECORDED, unreconciled, ledgered OUT Payments to link.
 * - `supplierInvoices`: supplier invoices to settle; the service records one OUT
 *   Payment per distinct supplier and links those payments automatically (callers
 *   never manage Payments directly).
 * - `creditNoteRefunds`: credit notes to refund; the service records one OUT
 *   Payment per distinct client (grouped by the credit note's client) and links
 *   those payments automatically. Only valid on a BUSINESS bank line - a TRUST
 *   bank line rejects a non-empty array with 400 (a credit note is a
 *   BUSINESS-ledger receivable, never a trust position).
 * - `supplierAllocations`: document-free supplier mints - an on-account payment
 *   to a supplier with no supplier invoice behind it. BUSINESS bank lines
 *   only - a TRUST bank line rejects a non-empty array with 400 (a supplier is
 *   a BUSINESS-ledger payable, never a trust position). See
 *   `ReconcileSupplierAllocationSchema`.
 * - `clientAllocations`: document-free client mints, valid on either bank
 *   kind - a trust payment on a TRUST bank line, or a client debit that
 *   drains a document-free overpayment credit (no credit note required) on a
 *   BUSINESS bank line. Use `creditNoteRefunds` instead when refunding
 *   against an existing credit note. See `ReconcileClientAllocationSchema`.
 * - `splits`: free-form ledger account targets for any residual. Signed
 *   minor-unit amounts, debit-positive.
 *
 * Balance equation: `line.amountMinorUnits + splitSum + clearingSum === 0`, where
 * `clearingSum` is the sum of all linked OUT payment amounts (positive,
 * including minted supplier-invoice, credit-note-refund, supplier-allocation,
 * and client-allocation payments), and `line.amountMinorUnits` is negative
 * (money-out). Contrast money-in where `line.amountMinorUnits + splitSum -
 * clearingSum === 0`.
 */
export const ReconcileMoneyOutInputSchema = z.object({
    /**
     * IDs of already-recorded OUT Payments to link to this bank line. Each must be
     * RECORDED, unreconciled (bankStatementLineId is null), and own a settlement
     * journal (ledgerEntrySetId is non-null). Capped at 50.
     */
    paymentIds: z.array(z.string().uuid()).max(50).default([]),
    /**
     * Supplier invoices to settle. No duplicate supplierInvoiceId; each amount must
     * not exceed the invoice's outstanding balance. Under the hood the service records
     * one OUT Payment per distinct supplier (posting the AP-clearing journal) and links
     * it to this line - callers do not manage Payments directly. Capped at 50.
     */
    supplierInvoices: z.array(ReconcileMoneyOutSupplierInvoiceSchema).max(50).default([]),
    /**
     * Credit notes to refund. No duplicate creditNoteId; every credit note in the
     * array must share one currency; each amount must not exceed the credit
     * note's own refundable balance. Under the hood the service groups by
     * client and records one OUT Payment per distinct client (posting the
     * AR-clearing journal, creditNoteId-tagged) and links it to this line -
     * callers do not manage Payments directly. Only valid on a BUSINESS bank
     * line. Capped at 50.
     */
    creditNoteRefunds: z.array(ReconcileMoneyOutCreditNoteRefundSchema).max(50).default([]),
    /**
     * Document-free supplier allocations to mint: an on-account payment to a
     * supplier with no supplier invoice behind the amount, posting Dr Accounts
     * Payable (supplierId-tagged, document-untagged) - see
     * `ReconcileSupplierAllocationSchema` for the full BUSINESS-only resolution
     * table. No duplicate supplierId. Defaults to an empty array - omit
     * entirely when not paying a supplier on-account directly. Capped at 50.
     */
    supplierAllocations: z.array(ReconcileSupplierAllocationSchema).max(50).default([]),
    /**
     * Client allocations to mint: a trust payment out of the client's trust
     * balance on a TRUST bank line, or a document-free client debit that drains
     * an overpayment credit (no credit note required) on a BUSINESS bank line -
     * see `ReconcileClientAllocationSchema` for the full resolution table. No
     * duplicate clientId. Defaults to an empty array - omit entirely when not
     * paying a client directly. Capped at 50.
     */
    clientAllocations: z.array(ReconcileClientAllocationSchema).max(50).default([]),
    /**
     * Free-form ledger account splits for any residual amount. The clearing leg
     * covers all payment amounts; any gap between the bank line amount and the
     * clearing sum must be covered by splits. Capped at 50.
     */
    splits: z.array(AllocateBankStatementLineSplitSchema).max(50).default([]),
    /**
     * Caller-supplied idempotency key. A retry with the same key is a no-op.
     * Use a UUID generated once per reconcile intent and keep it stable across
     * retries.
     */
    idempotencyKey: z.string().min(1).max(255),
});
/**
 * `warnings` is `[]` when the reconcile raised nothing to flag, and carries one
 * plain-English entry per s 86(4) trust-investment conduct rule the posted
 * journal touched (money withdrawn from an investment account; a leg tagged with
 * a client other than the one the account is held for). They never block the
 * write - the backend records what happened - so they are a moment to steer the
 * operator, not an error. No PII: the text names rules, never a client.
 */
export const ReconcileMoneyOutResponseSchema = z.object({
    line: BankStatementLineSchema,
    entrySet: LedgerEntrySetSchema,
    warnings: z.array(z.string()),
});
// Batch reconcile N bank statement lines, each with its own caller-supplied splits[].
// The client computes each line's legs (mirroring the single-line chip). The service
// dispatches each allocation's splits to the sign-appropriate splits-only reconcile
// arm, which enforces zero-sum balance and ownership. Per-line failures are returned
// in `failed` rather than aborting the whole call.
export const BatchReconcileBankStatementLinesInputSchema = z.object({
    allocations: z.array(z.object({
        lineId: z.string().uuid(),
        splits: z.array(AllocateBankStatementLineSplitSchema).min(1).max(50),
    })).min(1).max(100),
    idempotencyKey: z.string().min(1).max(255),
});
export const BatchReconcileBankStatementLinesResponseSchema = z.object({
    succeeded: z.array(z.object({
        lineId: z.string().uuid(),
        ledgerEntrySetId: z.string().uuid(),
        /**
         * Per-line conduct warnings, in the same shape and with the same meaning as
         * the single-line reconcile responses: `[]` for an ordinary line, one plain-
         * English entry per s 86(4) trust-investment rule the posted journal touched.
         * They are carried per succeeded line rather than once for the batch because
         * a batch commonly drains a mixed statement, and a flat list would leave the
         * operator unable to tell which line the warning is about. They never block
         * the write. No PII: the text names rules, never a client.
         */
        warnings: z.array(z.string()),
    })),
    failed: z.array(z.object({
        lineId: z.string().uuid(),
        errorCode: z.string(),
        errorMessage: z.string(),
    })),
});
// =============================================================================
// Bank Statement Line - Legacy convergence (super-admin)
// =============================================================================
//
// Converges a legacy line (`importEntrySetId === null`, predating the
// two-account import model, or minted while its bank had no resolvable
// ledger) onto the imported shape: `Dr Bank / Cr Bank Suspense` posted at
// import time. Two building-block endpoints, mirroring the
// settlement-journal / resplit-settlement-journal pair - see
// `apps/backend/platform/src/domains/invoice/README.md` § "Super-admin endpoints
// for historic invoices" and `domains/bank-statement-line/README.md`.
/**
 * Body for `POST .../bank-statement-lines/:lineId/import-journal` - repairs an
 * UNALLOCATED legacy line by posting its missing import journal in place.
 * `expectedAmountMinorUnits` is an OPTIONAL operator assertion: when supplied,
 * it must match the line's own `amountMinorUnits` or the request is rejected
 * with 400 before any write. Omit it to skip the assertion.
 */
export const PostBankStatementLineImportJournalInputSchema = z.object({
    expectedAmountMinorUnits: z.number().int().optional(),
});
/**
 * Body for `POST .../bank-statement-lines/:lineId/reshape-reconcile-journal` -
 * converges an ALLOCATED legacy line's fused reconcile journal (which drains
 * the physical bank account directly) onto the two-account shape (drains Bank
 * Suspense instead), reversing and reposting the same journal in place.
 * `expectedAmountMinorUnits` is REQUIRED (unlike the sibling's optional
 * assertion): this endpoint reverses an already-posted journal, so a
 * hand-driven call must prove its target before any write.
 */
export const ReshapeBankStatementLineReconcileJournalInputSchema = z.object({
    expectedAmountMinorUnits: z.number().int(),
});
/**
 * Shared response for both convergence endpoints - the primitive's own
 * result, NOT the line shape (`importEntrySetId` is deliberately never
 * serialised on any line response, so per-line success is asserted from this
 * response alone). `outcome: 'already-imported'` is a 200 no-op: the line was
 * already converged (by a prior endpoint call or the CLI backfill) and nothing
 * was written this time. `ledgerEntrySetId` is only
 * present on the reshape endpoint's response (the repaired repair-only
 * endpoint never touches the allocation link).
 */
export const BankStatementLineConvergenceResponseSchema = z.object({
    outcome: z.enum(['posted', 'reshaped', 'already-imported']),
    importEntrySetId: z.string(),
    ledgerEntrySetId: z.string().optional(),
});
/**
 * One row in the fleet-wide legacy-import discovery list
 * (`GET /admin/bank-statement-lines/legacy-imports`): a null-import line on a
 * bank that HAS a bank-role ledger link (so the convergence primitive can
 * actually resolve its bank). `allocated` tells the caller (the CLI backfill
 * driver) which convergence endpoint to call - `import-journal` when false,
 * `reshape-reconcile-journal` when true.
 */
export const LegacyBankStatementLineRowSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    bankAccountId: z.string(),
    allocated: z.boolean(),
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    date: z.string(),
});
export const ListLegacyBankStatementLineImportsQuerySchema = z.object({
    cursor: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(50),
});
export const ListLegacyBankStatementLineImportsResponseSchema = createPaginatedResponseSchema(LegacyBankStatementLineRowSchema);
// =============================================================================
// Bank Statement Line - Allocation Suggestions
// =============================================================================
/**
 * A single allocation suggestion returned by the batch suggest-allocations
 * endpoint. Three kinds:
 *   - `invoice`:     an exact-amount open invoice corroborated by an identifier
 *                   in the bank description (money-in lines only).
 *   - `expense`:    an exact-amount APPROVED expense corroborated by an
 *                   identifier in the description (money-out, super-admin only).
 *   - `credit-note`: an exact-amount outstanding credit note corroborated by an
 *                   identifier in the description (money-out lines only).
 *
 * The matched document is embedded in full so the caller can render the
 * suggestion UI without a secondary fetch.
 *
 * `reason: 'exact amount'` (shared by all three kinds) marks a match made on
 * the outstanding amount alone, with no identifier in the line description to
 * corroborate it - only a producer that retrieves documents without
 * identifier corroboration emits it.
 */
export const SuggestedAllocationLegSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('invoice'),
        match: MatchableInvoiceSchema,
        reason: z.enum(['invoice number', 'payment reference', 'reference', "client's name", 'exact amount']),
    }),
    z.object({
        kind: z.literal('expense'),
        match: MatchableExpenseSchema,
        reason: z.enum(["supplier's name", 'reference', 'exact amount']),
    }),
    z.object({
        kind: z.literal('credit-note'),
        match: MatchableCreditNoteSchema,
        reason: z.enum(['credit note number', "client's name", 'exact amount']),
    }),
]);
/**
 * The same three document chips, but for a leg REHYDRATED from a persisted
 * proposal rather than computed live - so `reason` is an open string, not the
 * closed token enum above.
 *
 * The difference is who owns the vocabulary. On the live path the route itself
 * derives the reason, so the set of values is closed and the enum is worth
 * having. A rehydrated chip instead echoes the stored `matchReason` on the
 * proposal leg, and that value belongs to whichever producer wrote it: the
 * cascade's explainer wrote one of the tokens above, and `bank-allocation-rf`'s
 * writes a whole sentence ("The invoice's number or reference appears in the
 * statement line, and its outstanding amount matches exactly."). Both are
 * already renderable - `describeAllocationMatchReason` in `@packages/copy`
 * looks a token up in the copy catalog and passes anything else through
 * unchanged, which is exactly the token-or-sentence split this type encodes.
 *
 * Typing this as the closed enum was a live 500: the rehydration service cast
 * the stored string to the enum on the stated grounds that "no scorer path
 * stores an out-of-range reason string", which stopped being true the moment a
 * producer with a prose explainer went LIVE. Fastify's response serialiser
 * rejected the sentence and the whole by-lines batch failed. Keep this open.
 */
export const RehydratedAllocationLegSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('invoice'), match: MatchableInvoiceSchema, reason: z.string() }),
    z.object({ kind: z.literal('expense'), match: MatchableExpenseSchema, reason: z.string() }),
    z.object({ kind: z.literal('credit-note'), match: MatchableCreditNoteSchema, reason: z.string() }),
]);
/**
 * Input for the batch suggest-allocations endpoint.
 * Up to 100 line IDs per call; de-duplicated server-side.
 */
export const BatchSuggestAllocationsInputSchema = z.object({
    lineIds: z.array(z.string().uuid()).min(1).max(100),
});
/**
 * The suggestion result for a single bank statement line. `suggestion` is null
 * when the line is already allocated, has zero amount, sits on a bank account
 * with no business-document corpus (unresolvable ledger account, or a trust
 * bank), or no candidate matches. A line that does not belong to the requested
 * bank account is NOT represented here - it rejects the whole batch with 400.
 */
export const LineSuggestionSchema = z.object({
    lineId: z.string().uuid(),
    suggestion: SuggestedAllocationLegSchema.nullable(),
});
export const BatchSuggestAllocationsResponseSchema = z.object({
    suggestions: z.array(LineSuggestionSchema),
});
// =============================================================================
// Bank Statement Line - Reconcilable Payments
// GET /accounts/:accountId/bank-statement-lines/:lineId/reconcilable-payments
// =============================================================================
/**
 * Query parameters for the reconcilable-payments read.
 */
export const ListReconcilablePaymentsQuerySchema = z.object({
    /** Maximum number of payments to return. Default 50, max 100. */
    limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
/**
 * A single unreconciled ledgered RECORDED payment that can be drained against a
 * money-in bank statement line via Payments Clearing.
 *
 * Only payments with `bankStatementLineId IS NULL` and `ledgerEntrySetId IS NOT NULL`
 * appear here – i.e. the AR-clearing journal (Cr AR / Dr Clearing) has been posted
 * but no bank line has drained Clearing yet.
 */
export const ReconcilablePaymentItemSchema = z.object({
    paymentId: z.string().uuid(),
    clientId: z.string().uuid(),
    /** Display name of the client who made this payment. */
    clientName: z.string(),
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    /** ISO 8601 string of when the payment was recorded. */
    paidAt: z.string(),
    reference: z.string().nullable(),
});
export const ListReconcilablePaymentsResponseSchema = z.object({
    data: z.array(ReconcilablePaymentItemSchema),
});
// =============================================================================
// Bank Statement Line - Reconcilable Invoices
// GET /accounts/:accountId/bank-statement-lines/:lineId/reconcilable-invoices
// =============================================================================
/**
 * A single invoice that can be linked to a money-in bank statement line.
 *
 * `classification` drives the two settlement paths:
 *   - `'unpaid'`: no existing recorded payment; selecting this row will mint a new
 *     payment from the bank line (Payments Clearing credit + AR debit).
 *   - `'paid'`: has an unreconciled ledgered RECORDED payment in Payments Clearing;
 *     selecting this row drains Clearing via the bank line (bank debit + Clearing credit).
 *
 * `linkedPayment` is present only when `classification === 'paid'` and carries
 * the payment context needed to perform the drain (paymentId and amounts).
 */
export const ReconcilableInvoiceSchema = z.object({
    invoiceId: z.string().uuid(),
    invoiceNumber: z.string().nullable(),
    paymentReference: z.string().nullable(),
    reference: z.string().nullable(),
    clientId: z.string().uuid(),
    /** Display name of the client associated with this invoice. */
    clientName: z.string(),
    totalMinorUnits: z.number().int(),
    /**
     * Remaining balance on the invoice: total minus every settlement recorded
     * against it (legacy bank-line matches, recorded payments), read from the
     * ledger's tagged Accounts Receivable net. One definition serves both
     * classifications - the reconcile state of the payment(s) that settled the
     * invoice never affects this figure. A `'paid'` row can still be > 0 for a
     * partially-paid invoice; it reads 0 once every settlement, reconciled or
     * not, covers the total.
     */
    outstandingMinorUnits: z.number().int(),
    currency: z.string(),
    /** ISO 8601 date string (invoice date). */
    date: z.string(),
    status: z.enum(['APPROVED', 'SENT', 'PAID']),
    classification: z.enum(['unpaid', 'paid']),
    /**
     * Present only when `classification === 'paid'`. Describes the recorded
     * payment that already covers this invoice (but has not yet been reconciled
     * to a bank line via Payments Clearing).
     */
    linkedPayment: z
        .object({
        paymentId: z.string().uuid(),
        /** Amount allocated from this payment specifically to this invoice. */
        amountAllocated: z.number().int(),
        /**
         * Full amount of the payment. This is the amount that drains Payments
         * Clearing when the payment is reconciled to the bank line.
         */
        paymentTotalMinorUnits: z.number().int(),
    })
        .optional(),
});
/**
 * Query parameters for the reconcilable-invoices read.
 */
export const ReconcilableInvoiceQuerySchema = z.object({
    /** Case-insensitive substring search across invoice number, payment reference, project reference, and client name. Default: empty (no filter). */
    search: z.string().trim().max(200).optional().default(''),
    /** Maximum number of invoices to return. Default 50, max 100. */
    limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
export const ReconcilableInvoiceResponseSchema = z.object({
    data: z.array(ReconcilableInvoiceSchema),
});
// =============================================================================
// Bank Statement Line - Reconcilable Expense Payments
// GET /accounts/:accountId/bank-statement-lines/:lineId/reconcilable-expense-payments
// =============================================================================
/**
 * Query parameters for the reconcilable-expense-payments read.
 */
export const ListReconcilableExpensePaymentsQuerySchema = z.object({
    /** Maximum number of payments to return. Default 50, max 100. */
    limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
/**
 * A single unreconciled ledgered OUT RECORDED payment that can be drained
 * against a money-out bank statement line via Payments Clearing.
 *
 * Only payments with `direction:'OUT'`, `bankStatementLineId IS NULL`, and
 * `ledgerEntrySetId IS NOT NULL` appear here - i.e. the AP-clearing journal
 * (Dr AP / Cr Clearing) has been posted but no bank line has drained
 * Clearing yet.
 */
export const ReconcilableExpensePaymentItemSchema = z.object({
    paymentId: z.string().uuid(),
    supplierId: z.string().uuid(),
    /** Display name of the supplier associated with this payment. */
    supplierName: z.string(),
    amountMinorUnits: z.number().int(),
    currency: z.string(),
    /** ISO 8601 string of when the payment was recorded. */
    paidAt: z.string(),
    reference: z.string().nullable(),
});
export const ListReconcilableExpensePaymentsResponseSchema = z.object({
    data: z.array(ReconcilableExpensePaymentItemSchema),
});
// =============================================================================
// Bank Statement Line - Reconcilable Supplier Invoices
// GET /accounts/:accountId/bank-statement-lines/:lineId/reconcilable-supplier-invoices
// =============================================================================
/**
 * A single supplier invoice (expense) that can be linked to a money-out bank
 * statement line via the Payments Clearing drain path.
 *
 * `classification` drives the two settlement paths:
 *   - `'unpaid'`: no existing recorded OUT payment; selecting this row will mint
 *     a new OUT payment from the bank line (Payments Clearing credit + AP debit).
 *   - `'paid'`: has an unreconciled ledgered RECORDED OUT payment in Payments
 *     Clearing; selecting this row drains Clearing via the bank line (bank credit
 *     + Clearing debit).
 *
 * `linkedPayment` is present only when `classification === 'paid'` and carries
 * the payment context needed to perform the drain (paymentId and amounts).
 */
export const ReconcilableSupplierInvoiceSchema = z.object({
    supplierInvoiceId: z.string().uuid(),
    supplierId: z.string().uuid(),
    /** Display name of the supplier associated with this expense. */
    supplierName: z.string(),
    supplierInvoiceNumber: z.string().nullable(),
    /**
     * The other references this expense carries. Surfaced on the money-out
     * reconcile picker so a row can be identified (and searched) by whichever
     * reference the bank narration quotes.
     */
    supplierOwnReference: z.string().nullable(),
    customerReference: z.string().nullable(),
    customerAccountNumber: z.string().nullable(),
    totalMinorUnits: z.number().int(),
    /**
     * Remaining balance on the supplier invoice: total minus every settlement
     * recorded against it (legacy bank-line matches, recorded payments), read
     * from the ledger's tagged Accounts Payable net. One definition serves both
     * classifications - the reconcile state of the payment(s) that settled the
     * expense never affects this figure. A `'paid'` row can still be > 0 for a
     * partially-paid expense; it reads 0 once every settlement, reconciled or
     * not, covers the total.
     */
    outstandingMinorUnits: z.number().int(),
    currency: z.string(),
    /** ISO 8601 date string (expense date). */
    date: z.string(),
    status: z.enum(['APPROVED', 'PAID']),
    classification: z.enum(['unpaid', 'paid']),
    /**
     * Present only when `classification === 'paid'`. Describes the recorded OUT
     * payment that already covers this expense (but has not yet been reconciled
     * to a bank line via Payments Clearing).
     */
    linkedPayment: z
        .object({
        paymentId: z.string().uuid(),
        /** Amount allocated from this payment specifically to this supplier invoice. */
        amountAllocated: z.number().int(),
        /**
         * Full amount of the payment. This is the amount that drains Payments
         * Clearing when the payment is reconciled to the bank line.
         */
        paymentTotalMinorUnits: z.number().int(),
    })
        .optional(),
});
/**
 * Query parameters for the reconcilable-supplier-invoices read.
 */
export const ReconcilableSupplierInvoiceQuerySchema = z.object({
    /** Case-insensitive substring search across supplier name and supplier reference. Default: empty (no filter). */
    search: z.string().trim().max(200).optional().default(''),
    /** Maximum number of supplier invoices to return. Default 50, max 100. */
    limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
export const ReconcilableSupplierInvoiceResponseSchema = z.object({
    data: z.array(ReconcilableSupplierInvoiceSchema),
});
// =============================================================================
// Bank Account - Balance Summary
// GET /accounts/:accountId/bank-accounts/:bankAccountId/balance-summary
//
// Presents a bank account's per-bank Payments Clearing balance as "the bank
// account's pending money": what has been recorded as a client or supplier
// payment but not yet confirmed on the bank's own statement. See
// apps/backend/platform/src/domains/bank-statement-line
// (getBankAccountBalanceSummary) for the full composition.
// =============================================================================
/**
 * One unreconciled recorded payment contributing to the pending balance - the
 * client-tagged IN pool and supplier-tagged OUT pool merged into a single
 * presentation shape, ordered by `paidAt` from the underlying readers.
 */
export const RecordedPaymentPendingItemSchema = z.object({
    paymentId: z.string().uuid(),
    direction: z.enum(['IN', 'OUT']),
    /** Client (IN) or supplier (OUT) display name. */
    counterpartyName: z.string(),
    /**
     * The client (IN) or supplier (OUT) id. `.optional()` only so a new bundle
     * safe-parses an old server's response during a deploy window; the backend
     * always sets it.
     * TODO: make it required once the release adding it is live.
     */
    counterpartyId: z.uuid().optional(),
    /** Signed clearing contribution: IN > 0 (Dr clearing), OUT < 0 (Cr clearing). */
    clearingAmountMinorUnits: z.number().int(),
    /** ISO 8601 string of when the payment was recorded. */
    paidAt: z.string(),
    reference: z.string().nullable(),
});
/**
 * Response for GET /accounts/:accountId/bank-accounts/:bankAccountId/balance-summary.
 *
 * `pendingBalanceMinorUnits` (the clearing GL account balance, signed: +
 * net money-in pending, - net money-out pending) is the AUTHORITATIVE pending
 * figure - render it as-is. `recordedPayments` is a best-effort itemisation
 * that can under-count a manually journalled clearing entry; treat it as an
 * illustrative drill-down, not a second source of truth.
 *
 * `clearingLedgerAccountId` is null only when the bank account has no active
 * matching-kind ledger. In that case every balance is 0 and the
 * `recordedPayments` array is empty. Once a matching-kind ledger exists, missing
 * or ambiguous bank-scoped accounts are surfaced as configuration failures.
 */
export const BankAccountBalanceSummaryResponseSchema = z.object({
    currency: z.string(),
    /** BANK-role GL account balance - what has cleared and posted. */
    confirmedBalanceMinorUnits: z.number().int(),
    /** Clearing GL account balance, signed (+ in / - out). Authoritative. */
    pendingBalanceMinorUnits: z.number().int(),
    /** Bank Suspense GL balance with its sign inverted to the bank-facing direction. */
    unallocatedBalanceMinorUnits: z.number().int(),
    clearingLedgerAccountId: z.string().uuid().nullable(),
    recordedPayments: z.array(RecordedPaymentPendingItemSchema),
});
// =============================================================================
// Bank Account - Batched Metrics
// POST /accounts/:accountId/bank-accounts/<metric>
//
// Batched single-purpose siblings of the per-bank metric reads above. Responses
// are sparse: an id outside the authorised account scope is absent.
// =============================================================================
export const BankAccountIdsInputSchema = z.object({
    bankAccountIds: z.array(z.string().uuid()).min(1).max(100),
});
/**
 * Nullable, mirroring the backend's own type (`ConfirmedBankBalance` in
 * `ledger.service.bank-balances.ts`) - `null` means the bank account has no
 * linked BANK-role ledger account, so there is no confirmed balance to carry.
 * A linked bank always carries its confirmed balance and currency together.
 *
 * This used to be a `linked`-discriminated union, which needed
 * `z.literal(true)`/`z.literal(false)` branch keys - rejected outright by
 * zod-compat/no-zod-boolean-literal (boolean literals compile to a JSON
 * Schema enum that Gemini's Vertex function-calling API rejects). Nullable
 * sidesteps the boolean entirely: no literal keys, and the linked/unlinked
 * distinction is still exhaustively representable.
 */
export const BankAccountConfirmedBalanceSchema = z
    .object({
    confirmedBalanceMinorUnits: z.number().int(),
    currency: z.string(),
})
    .nullable();
export const BankAccountConfirmedBalancesResponseSchema = z.object({
    balances: z.object({}).catchall(BankAccountConfirmedBalanceSchema),
});
export const BankAccountUnallocatedCountsResponseSchema = z.object({
    counts: z.object({}).catchall(BankAccountAllocationCountSchema),
});
export const BankAccountImportedLineCoverageBatchResponseSchema = z.object({
    coverage: z.object({}).catchall(BankAccountImportedLineCoverageSchema),
});
// =============================================================================
// Bank Statement Line - Client Allocations
// GET /accounts/:accountId/bank-statement-lines/:lineId/client-allocations
// =============================================================================
/**
 * One document-free client allocation payment linked to a bank statement line.
 * The `ledgerKind` identifies whether the line belongs to the BUSINESS or
 * TRUST ledger. The read-back sibling of `MatchedInvoiceSchema` /
 * `MatchedExpenseSchema` for the `clientAllocations` reconcile leg.
 */
export const ClientAllocationForLineSchema = z.object({
    clientId: z.string().uuid(),
    clientName: z.string(),
    amountMinorUnits: z.number().int(),
    /** Which ledger the mint posted to - BUSINESS (client credit) or TRUST (receipt/payment). */
    ledgerKind: LedgerKindSchema,
});
// =============================================================================
// Bank Statement Line - Allocation details (batched)
// POST /accounts/:accountId/bank-statement-lines/allocation-details
// =============================================================================
/**
 * What unmatching the line does to one allocated item, as the unmatch itself
 * will enact it (the payment domain's void-on-unmatch rule decides):
 * - `REVERSED`: every payment settling the item is voided, so it reverts.
 * - `KEPT`: every payment settling the item survives, unlinked from the line,
 *   so the item stays settled.
 * - `PARTLY_REVERSED`: the line settles the item with both kinds of payment.
 * An item with no payment (the legacy tagged-leg shape) is `REVERSED`.
 */
export const UnmatchItemOutcomeSchema = z.enum(['REVERSED', 'KEPT', 'PARTLY_REVERSED']);
/** A client allocation, supplier allocation or credit-note refund is exactly one payment, so it is never partly reversed. */
export const SinglePaymentUnmatchOutcomeSchema = z.enum(['REVERSED', 'KEPT']);
/**
 * Everything an allocated bank statement line's row renders, for one line.
 *
 * Each field is the same shape the corresponding singular GET returns, so a
 * caller can move between the two without reshaping. Those GETs remain - this
 * is the page-shaped read the reconciliation table needs, not a replacement.
 */
export const AllocationDetailsForLineSchema = z.object({
    invoices: z.array(MatchedInvoiceSchema.extend({ unmatchOutcome: UnmatchItemOutcomeSchema })),
    expenses: z.array(MatchedExpenseSchema.extend({ unmatchOutcome: UnmatchItemOutcomeSchema })),
    creditNotes: z.array(MatchedCreditNoteForLineSchema.extend({ unmatchOutcome: SinglePaymentUnmatchOutcomeSchema })),
    clientAllocations: z.array(ClientAllocationForLineSchema.extend({ unmatchOutcome: SinglePaymentUnmatchOutcomeSchema })),
    transfer: LineTransferSchema.nullable(),
    /**
     * RAW ledger account ids on the line's journal, internal legs included.
     *
     * The caller filters out the clearing / suspense / transfer legs against its
     * own resolved role set. That classification is deliberately not done here:
     * it needs the caller's internal-account set, and a hinged allocation set
     * contains the clearing account twice, so a naive server-side filter would
     * have to reimplement a rule that already lives (and is tested) on the
     * client.
     */
    ledgerAccountIds: z.array(z.string().uuid()),
});
/**
 * Sparse map keyed by bank statement line id - a line with nothing to report
 * (unallocated, or allocated to free-form ledger accounts only) is absent
 * rather than present-and-empty, so the caller can tell "nothing here" from
 * "not asked for". Lines belonging to another account are likewise absent.
 */
export const BankStatementLineAllocationDetailsResponseSchema = z.object({
    details: z.object({}).catchall(AllocationDetailsForLineSchema),
});
/**
 * Bounded by the transactions list's own page cap, so one rendered page is one
 * request. The id array is why this is a POST: it does not belong in a query
 * string, and the endpoint creates nothing, so it takes no idempotency key.
 */
export const BankStatementLineAllocationDetailsInputSchema = z.object({
    lineIds: z.array(z.string().uuid()).max(100),
});
// =============================================================================
// Bank Statement Line - Supplier Allocations
// GET /accounts/:accountId/bank-statement-lines/:lineId/supplier-allocations
// =============================================================================
/**
 * One document-free supplier allocation payment linked to a bank statement
 * line - the supplier-party analogue of `ClientAllocationForLineSchema`.
 * Carries no `ledgerKind`: unlike a client mint, a supplier mint is
 * BUSINESS-only (`recordPayment` throws on TRUST + `supplierId`), so the
 * field would always read `'BUSINESS'` - a constant the response omits by
 * design rather than serialise a value with no second state to distinguish.
 */
export const SupplierAllocationForLineSchema = z.object({
    supplierId: z.string().uuid(),
    supplierName: z.string(),
    amountMinorUnits: z.number().int(),
    /** Whether unmatching the line voids this payment. Exactly one payment, so never partly reversed. */
    unmatchOutcome: SinglePaymentUnmatchOutcomeSchema,
});
export const ListSupplierAllocationsForLineResponseSchema = z.object({
    data: z.array(SupplierAllocationForLineSchema),
});
// =============================================================================
// Bank Statement Line - Trust interest / LPFF classification
//
// Four dumb, single-purpose routes recording the monthly trust-interest and
// LPFF liability cycle: an interest credit, the LPFF transfer debit, a trust
// bank charge debit, and a charge-related credit. Every route derives its
// whole journal server-side from the bank line alone - the request body
// carries only the idempotency key plus the one caller judgement each
// classification genuinely needs (a charge's attribution, or a credit's
// kind). Response shape mirrors `ReconcileMoneyInResponseSchema` /
// `ReconcileMoneyOutResponseSchema` so the Bill frontend's row-patch works
// unchanged across all six splits-only reconcile routes.
// =============================================================================
export const ClassifyTrustLineResponseSchema = z.object({
    line: BankStatementLineSchema,
    entrySet: LedgerEntrySetSchema,
    warnings: z.array(z.string()),
    /**
     * The same warnings as `warnings`, split into a title and a description.
     * Transitional: `warnings` also carries these four classify routes'
     * output because the field is shared with the already-shipped reconcile
     * routes (`ReconcileMoneyInResponseSchema` / `ReconcileMoneyOutResponseSchema`)
     * and `client-project-activity.ts`, and changing that shared field's type
     * would break an already-loaded Bill tab. Once those routes carry their
     * own structured warnings, `warnings` can retire from this schema.
     */
    warningDetails: z.array(WarningDetailSchema),
});
export const ClassifyTrustInterestInputSchema = z.object({
    idempotencyKey: z.string().min(1).max(255),
});
export const ClassifyLpffTransferInputSchema = z.object({
    idempotencyKey: z.string().min(1).max(255),
});
/**
 * Who bears a trust bank charge. `CLIENT` names the one client it is
 * recovered from; the other two kinds carry no further data. `FUND` on a
 * section 86(4) client trust investment account is a 400 (not encoded here -
 * the statutory-type gate needs the line's bank account, which the schema
 * cannot see), never on the pooled section 86(2) account or a future
 * section 86(3) one.
 */
export const TrustChargeAttributionSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('FUND') }),
    z.object({ kind: z.literal('FIRM') }),
    z.object({ kind: z.literal('CLIENT'), clientId: z.string().uuid() }),
]);
/**
 * Every attribution the classify-trust-charge route posts: the Bill UI menu's
 * `TrustChargeAttributionSchema` kinds plus `FUND_FIRM_SPLIT`, which records
 * one charge partly against the Fund and the remainder against the firm (the
 * shape where the Fund refunds a fee ex VAT and the firm bears the VAT). Kept
 * apart from the menu union so the UI's exhaustive per-kind maps do not have
 * to offer a split.
 *
 * The caller supplies the Fund's share; the server never assumes a VAT rate.
 * It must be less than the charge - that bound needs the line, so the service
 * enforces it - because a share of nothing or of the whole charge is the
 * `FIRM` or `FUND` attribution, and refused as such.
 */
export const TrustChargePostingAttributionSchema = z.discriminatedUnion('kind', [
    ...TrustChargeAttributionSchema.options,
    z.strictObject({ kind: z.literal('FUND_FIRM_SPLIT'), fundAmountMinorUnits: z.number().int().min(1) }),
]);
export const ClassifyTrustBankChargeInputSchema = z.object({
    idempotencyKey: z.string().min(1).max(255),
    attribution: TrustChargePostingAttributionSchema,
});
/**
 * Which real event a trust charge credit records, not which account the
 * operator recalls an earlier charge landing on - the operator names what
 * happened, and the account it credits is derived from that:
 *
 * - `VAT_REDIRECT`: the bank-initiated monthly redirect of VAT off a
 *   Fund-claimable charge to the business account - observed on the pooled
 *   trust bank account every month. Restores Trust Interest Payable
 *   to LPFF. Not valid on section 86(4), where charges are not
 *   Fund-claimable.
 * - `FUND_REFUND`: a bank refund or other credit against a Fund-claimable
 *   charge, outside the monthly VAT redirect. Also restores Trust Interest
 *   Payable to LPFF - `VAT_REDIRECT` and `FUND_REFUND` post identically, and
 *   are kept distinct only because they read as different events to the
 *   operator, the same reasoning `TrustChargeAttributionSchema` already
 *   follows for the charge side. Not valid on section 86(4), where charges
 *   are not Fund-claimable.
 * - `FIRM_REIMBURSEMENT`: the firm reimbursing the trust for a charge it
 *   bore itself. Restores the Recoverable from Business account.
 *
 * No option restores a `CLIENT`-attributed charge - a client-recoverable
 * charge is settled by the client's own trust position, not by a credit
 * through this route.
 */
export const TrustChargeCreditReasonSchema = z.enum(['VAT_REDIRECT', 'FUND_REFUND', 'FIRM_REIMBURSEMENT']);
/**
 * A credit restoring a previously recorded trust bank charge: the monthly
 * VAT redirect, a Fund-claimable refund, or a firm reimbursement.
 */
export const ClassifyTrustChargeCreditInputSchema = z.object({
    idempotencyKey: z.string().min(1).max(255),
    reason: TrustChargeCreditReasonSchema,
});
